import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Megaphone, Plus, Search } from "lucide-react";
import { fetchNotices } from "../../controllers/noticeController";
import { useNotices } from "../../components/notices/NoticeProvider";
import NoticeListItem from "../../components/notices/NoticeListItem";
import { noticeButton, noticeInput } from "../../components/notices/NoticeParts";
import { NOTICE_CATEGORIES, isLive } from "../../components/notices/noticeUtils";

const PAGE_SIZE = 15;

const STATUS_TABS = [
  ["active", "Live"],
  ["scheduled", "Scheduled"],
  ["expired", "Expired"],
  ["all", "All"],
];

const pill = (active) =>
  `inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
    active
      ? "border-blue-600 bg-blue-600 text-white dark:border-blue-500 dark:bg-blue-500"
      : "border-app-primary bg-app-card text-app-secondary hover:bg-app-hover"
  }`;

/** /notices — every notice, with filters. HR/admins can post and also browse scheduled and expired notices. */
function NoticeBoardPage() {
  const { version, unreadCount, pendingAckCount, canManage, openNotice, openEditor } = useNotices();
  const [params, setParams] = useSearchParams();
  const status = canManage ? params.get("status") || "active" : "active";
  const filter = params.get("filter") || "";
  const category = params.get("category") || "";
  const [search, setSearch] = useState(params.get("q") || "");
  const [debounced, setDebounced] = useState(search);
  const [state, setState] = useState({ loading: true, error: false, notices: [], total: 0, page: 1 });
  const requestId = useRef(0);

  // Deep link from a push notification or a toast: /notices?n=<id>
  useEffect(() => {
    const id = params.get("n");
    if (!id) return;
    openNotice(Number(id));
    setParams((p) => {
      p.delete("n");
      return p;
    }, { replace: true });
  }, [params, setParams, openNotice]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const updateParam = (key, value) =>
    setParams((p) => {
      if (value) p.set(key, value);
      else p.delete(key);
      return p;
    }, { replace: true });

  const load = async (page) => {
    const id = ++requestId.current;
    setState((s) => ({ ...s, loading: true, error: false }));
    try {
      const data = await fetchNotices({
        status,
        filter: filter || undefined,
        category: category || undefined,
        search: debounced || undefined,
        page,
        limit: PAGE_SIZE,
      });
      if (id !== requestId.current) return;
      setState((s) => ({
        loading: false,
        error: false,
        total: data.total,
        page,
        notices: page === 1 ? data.notices : [...s.notices, ...data.notices.filter((n) => !s.notices.some((o) => o.id === n.id))],
      }));
    } catch {
      if (id === requestId.current) setState((s) => ({ ...s, loading: false, error: true }));
    }
  };

  // Reload from the first page whenever filters change or any notice changes (live updates).
  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, filter, category, debounced, version]);

  const notices = status === "active" ? state.notices.filter((n) => isLive(n)) : state.notices;
  const hasMore = state.notices.length < state.total;
  const filtersActive = Boolean(filter || category || debounced);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5">
      <section data-tour="notice-header" className="flex flex-col gap-4 rounded-xl border border-app-primary bg-app-card p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-sky-300">
            <Megaphone className="h-6 w-6" aria-hidden="true" />
          </span>
          <div>
            <h2 className="text-xl font-semibold text-app-heading">Notice Board</h2>
            <p className="text-sm text-app-muted">
              {unreadCount > 0 ? `${unreadCount} unread` : "You're all caught up"}
              {pendingAckCount > 0 && ` · ${pendingAckCount} awaiting your acknowledgement`}
            </p>
          </div>
        </div>
        {canManage && (
          <button type="button" data-tour="notice-new" className={noticeButton.primary} onClick={() => openEditor()}>
            <Plus className="h-4 w-4" /> New notice
          </button>
        )}
      </section>

      {canManage && (
        <div data-tour="notice-status-tabs" className="flex gap-1 overflow-x-auto rounded-lg bg-app-hover p-1 text-sm" role="tablist" aria-label="Notice status">
          {STATUS_TABS.map(([key, text]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={status === key}
              onClick={() => setParams(key === "active" ? {} : { status: key }, { replace: true })}
              className={`flex-1 whitespace-nowrap rounded-md px-3 py-1.5 font-medium transition-colors ${
                status === key ? "bg-app-card text-app-heading shadow-sm" : "text-app-muted hover:text-app-primary"
              }`}
            >
              {text}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-3">
        <label className="relative block" data-tour="notice-search">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-app-muted" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search notices"
            aria-label="Search notices"
            className={`${noticeInput} pl-9`}
          />
        </label>
        <div className="flex gap-2 overflow-x-auto pb-1" data-tour="notice-filters">
          {status === "active" && (
            <>
              <button type="button" className={pill(!filter)} onClick={() => updateParam("filter", "")} aria-pressed={!filter}>
                All
              </button>
              <button type="button" className={pill(filter === "unread")} onClick={() => updateParam("filter", "unread")} aria-pressed={filter === "unread"}>
                Unread {unreadCount > 0 && <span className="opacity-80">{unreadCount}</span>}
              </button>
              <button
                type="button"
                className={pill(filter === "pending-ack")}
                onClick={() => updateParam("filter", "pending-ack")}
                aria-pressed={filter === "pending-ack"}
              >
                Needs acknowledgement {pendingAckCount > 0 && <span className="opacity-80">{pendingAckCount}</span>}
              </button>
              <span className="mx-1 w-px shrink-0 bg-app-hover" aria-hidden="true" />
            </>
          )}
          {Object.entries(NOTICE_CATEGORIES).map(([key, c]) => {
            const Icon = c.icon;
            const active = category === key;
            return (
              <button
                key={key}
                type="button"
                className={pill(active)}
                onClick={() => updateParam("category", active ? "" : key)}
                aria-pressed={active}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden="true" /> {c.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-3">
        {state.loading && state.page === 1 && notices.length === 0 &&
          [0, 1, 2, 3].map((i) => <div key={i} className="h-[112px] animate-pulse rounded-xl bg-app-hover" aria-hidden="true" />)}

        {state.error && (
          <div className="rounded-xl border border-app-error bg-app-error p-4 text-sm text-app-error">
            Couldn’t load notices.{" "}
            <button type="button" className="font-semibold underline" onClick={() => load(1)}>
              Try again
            </button>
          </div>
        )}

        {!state.loading && !state.error && notices.length === 0 && (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-app-primary px-6 py-14 text-center">
            <Megaphone className="h-10 w-10 text-app-muted opacity-60" aria-hidden="true" />
            <p className="text-sm font-medium text-app-secondary">
              {filtersActive
                ? "No notices match these filters."
                : status === "scheduled"
                  ? "Nothing scheduled."
                  : status === "expired"
                    ? "No expired notices."
                    : "No notices yet."}
            </p>
            {filtersActive ? (
              <button
                type="button"
                className={noticeButton.secondary}
                onClick={() => {
                  setSearch("");
                  setParams(canManage && status !== "active" ? { status } : {}, { replace: true });
                }}
              >
                Clear filters
              </button>
            ) : (
              canManage &&
              status === "active" && (
                <button type="button" className={noticeButton.primary} onClick={() => openEditor()}>
                  <Plus className="h-4 w-4" /> Post the first notice
                </button>
              )
            )}
          </div>
        )}

        {notices.map((notice) => (
          <NoticeListItem key={notice.id} notice={notice} onOpen={openNotice} showStats={canManage} />
        ))}

        {hasMore && !state.error && (
          <div className="flex justify-center pt-2">
            <button type="button" className={noticeButton.secondary} onClick={() => load(state.page + 1)} disabled={state.loading}>
              {state.loading ? "Loading…" : `Load more (${state.total - state.notices.length})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default NoticeBoardPage;
